import React, { useState } from "react";
import { useQuery } from "react-query";
import { toast } from "sonner";

import { bangTinApi } from "src/1/apis/bang_tin.api";

import BranchPerformanceTable from "./BranchPerformanceTable";
import DashboardSummaryCards from "./DashboardSummaryCards";
import DashboardToolbar from "./DashboardToolbar";
import { getDefaultDateRange, validateDateRange } from "./dashboardUtils";
import InventoryValueChart from "./InventoryValueChart";

export default function WarehouseDashboard() {
  const fontStyle = { fontFamily: "'Inter', sans-serif" };

  // State bộ lọc kỳ & ngày & chi nhánh
  const [periodType, setPeriodType] = useState("day"); // "day" | "month" | "year"
  const defaultDates = getDefaultDateRange("day");
  const [fromDate, setFromDate] = useState(defaultDates.fromDate);
  const [toDate, setToDate] = useState(defaultDates.toDate);
  const [selectedStoreCodes, setSelectedStoreCodes] = useState([]);

  // Tham số query dùng chung
  const storeCodesParam =
    selectedStoreCodes.length > 0 ? selectedStoreCodes.join(",") : undefined;

  const isRangeValid = validateDateRange(periodType, fromDate, toDate).valid;

  // 1. API: Tổng tiền tồn kho theo kỳ
  const {
    data: inventoryData,
    isLoading: isLoadingInventory,
    isFetching: isFetchingInventory,
    refetch: refetchInventory,
  } = useQuery(
    ["dashboard-inventory-value", periodType, fromDate, toDate, storeCodesParam],
    () =>
      bangTinApi.getInventoryValue({
        type: periodType,
        from_date: fromDate,
        to_date: toDate,
        store_codes: storeCodesParam,
      }),
    {
      enabled: isRangeValid,
      select: (res) => res?.data?.data ?? res?.data,
      refetchOnWindowFocus: false,
    }
  );

  // 2. API: Tổng tiền mua hàng theo kỳ
  const {
    data: purchaseData,
    isLoading: isLoadingPurchase,
    isFetching: isFetchingPurchase,
    refetch: refetchPurchase,
  } = useQuery(
    ["dashboard-purchase-value", fromDate, toDate, storeCodesParam, periodType],
    () =>
      bangTinApi.getPurchaseValue({
        from_date: fromDate,
        to_date: toDate,
        type: periodType,
        store_codes: storeCodesParam,
      }),
    {
      enabled: isRangeValid,
      select: (res) => res?.data?.data ?? res?.data,
      refetchOnWindowFocus: false,
    }
  );

  // 3. API: Chi tiết tồn kho theo kỳ (cho Biểu đồ xu hướng)
  const {
    data: inventoryDetailData,
    isLoading: isLoadingDetail,
    isFetching: isFetchingDetail,
    refetch: refetchDetail,
  } = useQuery(
    ["dashboard-inventory-detail", periodType, fromDate, toDate, storeCodesParam],
    () =>
      bangTinApi.getInventoryValueDetail({
        type: periodType,
        from_date: fromDate,
        to_date: toDate,
        store_codes: storeCodesParam,
      }),
    {
      enabled: isRangeValid,
      select: (res) => res?.data?.data ?? res?.data,
      refetchOnWindowFocus: false,
    }
  );

  // 4. API: Tổng tiền định mức tồn kho theo chi nhánh
  const {
    data: thresholdData,
    isLoading: isLoadingThreshold,
    isFetching: isFetchingThreshold,
    refetch: refetchThreshold,
  } = useQuery(
    ["dashboard-stock-threshold-value", storeCodesParam],
    () =>
      bangTinApi.getStockThresholdValue({
        store_codes: storeCodesParam,
      }),
    {
      select: (res) => res?.data?.data ?? res?.data,
      refetchOnWindowFocus: false,
    }
  );

  const isRefreshing =
    isFetchingInventory ||
    isFetchingPurchase ||
    isFetchingDetail ||
    isFetchingThreshold;

  const handleRefreshAll = async () => {
    try {
      await Promise.all([
        refetchInventory(),
        refetchPurchase(),
        refetchDetail(),
        refetchThreshold(),
      ]);
      toast.success("Đã làm mới dữ liệu thống kê thành công!");
    } catch (e) {
      toast.error("Có lỗi xảy ra khi làm mới dữ liệu.");
    }
  };

  return (
    <div
      className="!font-inter !space-y-5"
      style={fontStyle}
    >
      {/* 1. Bộ lọc kỳ, thời gian và chi nhánh */}
      <DashboardToolbar
        periodType={periodType}
        setPeriodType={setPeriodType}
        fromDate={fromDate}
        setFromDate={setFromDate}
        toDate={toDate}
        setToDate={setToDate}
        selectedStoreCodes={selectedStoreCodes}
        setSelectedStoreCodes={setSelectedStoreCodes}
        onRefresh={handleRefreshAll}
        isRefreshing={isRefreshing}
      />

      {/* 2. Thẻ KPI tổng quan: Tồn kho, Mua hàng, Định mức */}
      <DashboardSummaryCards
        inventoryData={inventoryData}
        isLoadingInventory={isLoadingInventory}
        purchaseData={purchaseData}
        isLoadingPurchase={isLoadingPurchase}
        thresholdData={thresholdData}
        isLoadingThreshold={isLoadingThreshold}
        periodType={periodType}
      />

      {/* 3. Biểu đồ biến động giá trị tồn kho theo kỳ */}
      <InventoryValueChart
        detailData={inventoryDetailData}
        isLoading={isLoadingDetail}
        periodType={periodType}
      />

      {/* 4. Bảng số liệu chi tiết theo từng chi nhánh */}
      <BranchPerformanceTable
        inventoryData={inventoryData}
        purchaseData={purchaseData}
        thresholdData={thresholdData}
        isLoading={isLoadingInventory || isLoadingPurchase || isLoadingThreshold}
      />
    </div>
  );
}
